import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { IdentityType } from 'src/app/services/config.service';
import { environment } from '../../../../environments/environment';

type Section = 'identity' | 'sales' | 'roles';

@Injectable()
export class UsersService {
    constructor(
        private http: HttpClient,
        private transLateService: TranslateService,
    ) {}

    getMe(sections: Section[]): Observable<Me> {
        const languageCode = this.transLateService.currentLang ? this.transLateService.currentLang : 'en-US';
        const headers = new HttpHeaders().append('accept-language', languageCode);
        let params = new HttpParams();
        sections.forEach((section: string) => {
            params = params.append('sections', section);
        });
        return this.http.get<Me>(`${environment.http.usersBaseUrl}users/me`, { headers, params }).pipe(
            map((me) => {
                // TODO Remove after fix in Service
                if (me.address?.houseNumber && me.address?.postalCode) {
                    me.address.houseNumber = me.address.houseNumber.replace('undefined', '');
                    me.address.postalCode = me.address.postalCode.replace('undefined', '');
                }

                me.theme = 'multibrand';

                return me;
            }),
        );
    }

    updateMe(updatableUserMe: UpdatableMe) {
        if (updatableUserMe?.address?.houseNumber) {
            updatableUserMe.address.houseNumber = String(updatableUserMe.address.houseNumber);
        }

        if (updatableUserMe?.address?.postalCode) {
            updatableUserMe.address.postalCode = String(updatableUserMe.address.postalCode);
        }

        if (updatableUserMe?.address?.region) {
            updatableUserMe.address.region = String(updatableUserMe.address.region);
        }
        delete updatableUserMe.identityType;

        return this.http.patch(`${environment.http.usersBaseUrl}users/me`, updatableUserMe);
    }

    updateConsumerAccount(updatableUserMe: UpdatableMe): Observable<{ sapCustomerNumber?: string }> {
        if (updatableUserMe?.address?.houseNumber) {
            updatableUserMe.address.houseNumber = String(updatableUserMe.address.houseNumber);
        }

        if (updatableUserMe?.address?.postalCode) {
            updatableUserMe.address.postalCode = String(updatableUserMe.address.postalCode);
        }

        if (updatableUserMe?.address?.region) {
            updatableUserMe.address.region = String(updatableUserMe.address.region);
        }

        const identityType = updatableUserMe.identityType;
        delete updatableUserMe.identityType;

        // 1. PATCH /users/me — persist user data first
        return this.http.patch(`${environment.http.usersBaseUrl}users/me`, updatableUserMe).pipe(
            // 2. GET /users/me — check whether a BP is already linked
            switchMap(() => this.getMe(['identity', 'sales'])),
            switchMap((me) => {
                const hasBp = !!me.company?.id;
                if (!hasBp && identityType === IdentityType.EK) {
                    // 3. POST /users/{id}/bp — create or link BP
                    return this.http.post<{ sapCustomerNumber?: string }>(
                        `${environment.http.usersV2BaseUrl}users/${me.id}/bp`,
                        {},
                    );
                }
                // BP already exists — return the customer number from the existing BP
                return of({ sapCustomerNumber: me.company?.id });
            }),
        );
    }

    createAvatar(avatar: Blob): Observable<{ blobName: string }> {
        const formData: FormData = new FormData();
        formData.append('file', avatar);

        return this.http.post<{ blobName: string }>(`${environment.http.usersBaseUrl}avatar`, formData);
    }

    deleteAvatar(): Observable<void> {
        return this.http.delete<void>(`${environment.http.usersBaseUrl}avatar`);
    }
}

export interface Me {
    id: string;
    loginId: string;
    avatarLink: string;
    gender: 'MALE' | 'FEMALE' | 'OTHER';
    name: {
        title: string;
        firstName: string;
        familyName: string;
    };
    address: {
        street: string;
        houseNumber: string;
        postalCode: string;
        city: string;
        countryCode: string;
    };
    company: {
        name: string;
        countryCode: string;
        id: string;
        vkorg: string;
    };
    contacts: {
        email: string;
        mobile: CustomerContactData | string;
        telephone: CustomerContactData | string;
    };
    userState: string;
    languageCode: string;
    locale: string;
    properties: { name: string; value: string }[];
    applicationRoles?: string[];
    businessRoles?: string[];
    contactId?: string;
    isLite?: boolean;
    customerName?: string;
    theme?: '' | 'multibrand' | 'viessmann';
}

export interface CustomerContactData {
    country: string;
    number: string;
    extension?: string;
}

export interface UpdatableMe {
    id?: string;
    loginId?: string;
    name?: {
        title?: string;
        firstName?: string;
        familyName?: string;
    };
    address?: {
        street?: string;
        houseNumber?: string;
        postalCode?: string;
        city?: string;
        countryCode?: string;
        region?: string;
    };
    contacts?: {
        email?: string;
        mobile?: CustomerContactData | string;
        telephone?: CustomerContactData | string;
    };
    language?: string;
    customerName?: string;
    identityType?: IdentityType;
}
